import React from "react";
import { useInView } from "react-intersection-observer";

import { useGetFieldShape, useTranslation } from "utils";

import { DocScanFieldRecord } from "models";

import {
  Card,
  Checkbox,
  Divider,
  Flex,
  Icon,
  MapStatic,
  STATUS_COLOR_MAP,
  STATUS_LABEL_MAP,
  TagNormal,
  Tooltip,
  Typography,
  buildRows,
  deriveMatchStatus,
  parseLocation,
} from "components";

export type FieldMatchCardProps = {
  className?: string;
  record: DocScanFieldRecord;
  isSelected: boolean;
  hideStatuses?: boolean;
  onToggle: (id: string) => void;
  onChangeField?: () => void;
  rightContent?: React.ReactNode;
  headerActions?: React.ReactNode;
  dataTestId?: string;
};

export const FieldMatchCard = ({
  record,
  isSelected,
  hideStatuses,
  onToggle,
  onChangeField,
  rightContent,
  headerActions,
  className,
  dataTestId,
}: FieldMatchCardProps) => {
  const { t } = useTranslation("docScans");
  const { ref, inView } = useInView({ triggerOnce: true });

  const fieldId = record.fieldId ?? "";
  const matchStatus = deriveMatchStatus(record);
  const rows = buildRows(record.farmFieldRaw, record.docFieldRaw, record.statuses);
  const fieldLocation = record.farmFieldRaw?.location ? parseLocation(record.farmFieldRaw.location) : undefined;

  const fieldName = record.farmFieldRaw?.name ?? "-";
  const fieldNumber = record.farmFieldRaw?.number ?? "-";
  const fieldSize = record.farmFieldRaw?.size;

  const { fieldShape } = useGetFieldShape({ fieldId, enabled: inView });

  const resolvedTooltip = matchStatus === "auto_matched" ? t("fieldMatchCard.statusTooltip.autoMatched") : undefined;

  return (
    <Card customRef={ref} className={`field-match-card ${className ?? ""}`} border dataTestId={dataTestId}>
      <Flex align="center" gap="1rem" className="field-match-card-header padding-100 padding-right-50">
        <Flex
          align="center"
          gap="1rem"
          flex={1}
          className={`field-match-card-field-info${!isSelected ? " faded" : ""}`}
          data-testid="field-match-card-field-info"
        >
          {fieldLocation && (
            <div className="map-thumbnail">
              {/* @ts-expect-error MapStatic needs TS conversion */}
              <MapStatic field={{ id: fieldId, location: fieldLocation, size: fieldSize }} geometry={fieldShape} location={fieldLocation} />
            </div>
          )}
          <Flex vertical flex={1}>
            <Typography variant="paragraph-l" data-testid="field-match-card-field-name">
              {fieldName}
            </Typography>
            <Typography variant="paragraph-s" color="grey-600" data-testid="field-match-card-field-meta">
              {t("fieldMatchCard.fieldMeta", { number: fieldNumber, size: fieldSize })}
            </Typography>
          </Flex>
        </Flex>

        <Flex align="center" gap="1rem">
          {headerActions ?? (
            <>
              {onChangeField && (
                <Typography
                  variant="paragraph-m"
                  color="grey-600"
                  bold
                  className="change-field-link pointer"
                  onClick={onChangeField}
                  data-testid="field-match-card-change-field"
                >
                  {t("fieldMatchCard.changeField")}
                </Typography>
              )}

              <Tooltip title={resolvedTooltip} disabled={!resolvedTooltip}>
                <TagNormal
                  color={STATUS_COLOR_MAP[matchStatus]}
                  className="margin-0"
                  text={t(`fieldMatchCard.${STATUS_LABEL_MAP[matchStatus]}`)}
                  dataTestId="field-match-card-status-tag"
                />
              </Tooltip>

              <Tooltip title={t(`fieldMatchCard.checkboxTooltip.${isSelected ? "remove" : "confirm"}`)}>
                <Checkbox checked={isSelected} onChange={() => onToggle(record.id)} />
              </Tooltip>
            </>
          )}
        </Flex>
      </Flex>

      <Divider className="field-match-card-divider" />

      <div className={`field-match-card-table${rightContent ? " field-match-card-table--split" : ""}`} data-testid="field-match-card-table">
        <div className="field-match-card-table-left">
          <div className="field-match-card-table-header">
            <div className="field-match-card-table-cell label-col" />
            <div className="field-match-card-table-cell klim-col" title={t("fieldMatchCard.columns.klimPlatform")}>
              {t("fieldMatchCard.columns.klimPlatform")}
            </div>
            {!rightContent && (
              <>
                <div className="field-match-card-table-cell doc-col" title={t("fieldMatchCard.columns.document")}>
                  {t("fieldMatchCard.columns.document")}
                </div>
                {!hideStatuses && <div className="field-match-card-table-cell status-col" />}
              </>
            )}
          </div>

          {rows.map((row) => (
            <div key={row.label} className="field-match-card-table-row" data-testid={`field-match-card-row-${row.label}`}>
              <div className="field-match-card-table-cell label-col" title={t(`fieldMatchCard.rows.${row.label}`)}>
                {t(`fieldMatchCard.rows.${row.label}`)}
              </div>
              <div className={`field-match-card-table-cell klim-col${!row.isMatching && !hideStatuses ? " bold" : ""}`} title={row.klimValue}>
                {row.klimValue}
              </div>
              {!rightContent && (
                <>
                  <div className={`field-match-card-table-cell doc-col${!row.isMatching && !hideStatuses ? " bold" : ""}`} title={row.documentValue}>
                    {row.documentValue}
                  </div>
                  {!hideStatuses && (
                    <div className="field-match-card-table-cell status-col" data-testid={`field-match-card-status-${row.label}`}>
                      {row.isMatching ? <Icon iconName="tabler/check" size="s" className="match-icon" color="#065c45" /> : <div className="mismatch-dot" />}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {rightContent && (
          <div className="field-match-card-table-right" data-testid="field-match-card-right-content">
            {rightContent}
          </div>
        )}
      </div>
    </Card>
  );
};
